/**
 * `<FurnaceBuildIndicator>` — user-facing build-on-Furnace pill
 * (Bridge-3 chrome surface).
 *
 * Sits in the workspace header BESIDE the existing `<VmStatusPill>`
 * (which surfaces the *connection* state). This component surfaces
 * the *build* state — "is a Furnace build in flight RIGHT NOW".
 * Two pills, two concerns:
 *
 *   - `<VmStatusPill>` answers "can I reach the Furnace?"
 *   - `<FurnaceBuildIndicator>` answers "is the agent loop running
 *     on the Furnace right now?"
 *
 * Both render the connection / build state of the cohort cloud-build
 * rollout — see `docs/onboarding-furnace.md` for the engineer-facing
 * contract.
 *
 * Flag-OFF safety: the indicator returns `null` whenever
 * `state.routedToFurnace === false`, which is the case for every
 * flag-OFF + out-of-cohort + IPC-not-loaded combination. The chrome
 * is identical to pre-Bridge-3 main in those scenarios.
 *
 * Visual tokens follow the same family as `<VmStatusPill>` so the
 * two pills sit side-by-side without colliding visually.
 *
 * British spelling throughout.
 */

import type { FurnaceBuildState } from '../workspace/furnace-build-state';

/**
 * Visual token bundle per derived build phase. Mirrors
 * `VmStatusPill`'s `PILL_VISUALS` table shape.
 */
interface IndicatorVisual {
  glyph: string;
  /** Tailwind classes. Distinct from the VM-connection pill's
   *  colour family so the two pills can sit next to each other. */
  className: string;
  /** Aria label rendered as `aria-label`. */
  ariaLabel: string;
}

const PHASE_VISUALS: Record<FurnaceBuildState['phase'], IndicatorVisual> = {
  inactive: {
    glyph: '',
    className: '',
    ariaLabel: '',
  },
  queued: {
    glyph: '⏳',
    className: 'bg-sky-500/15 text-sky-200/90 border border-sky-500/30',
    ariaLabel: 'Queuing Furnace build',
  },
  building: {
    glyph: '🔨',
    className: 'bg-violet-500/15 text-violet-200/90 border border-violet-500/30',
    ariaLabel: 'Furnace build in progress',
  },
  reconnecting: {
    glyph: '🟠',
    className: 'bg-orange-500/15 text-orange-200/90 border border-orange-500/30',
    ariaLabel: 'Reconnecting to Furnace',
  },
  failed: {
    glyph: '🔴',
    className: 'bg-red-500/15 text-red-200/90 border border-red-500/30',
    ariaLabel: 'Furnace build failed',
  },
};

export interface FurnaceBuildIndicatorProps {
  /** Derived build state from `computeFurnaceBuildState`. The parent
   *  owns the input plumbing (FurnaceStatus query + chat reducer
   *  `isAiTyping` + VM connection subscription) so the component
   *  stays SSR-friendly. */
  state: FurnaceBuildState;
  /** Optional extra class names. Mirrors `<VmStatusPill>`'s
   *  passthrough — caller owns spacing, component owns colour /
   *  shape. */
  className?: string;
}

/**
 * The build-status pill. Hides whenever the derived phase is
 * `'inactive'` OR the routing gate is closed.
 */
export function FurnaceBuildIndicator({
  state,
  className = '',
}: FurnaceBuildIndicatorProps): React.JSX.Element | null {
  // Flag-OFF + out-of-cohort + no-IPC-yet → hide entirely. The
  // `routedToFurnace` bit is the load-bearing gate; we re-check
  // `phase === 'inactive'` as belt-and-braces (a future state
  // value with `routedToFurnace=true` but no in-flight turn must
  // still hide the pill).
  if (!state.routedToFurnace) return null;
  if (state.phase === 'inactive') return null;

  const visual = PHASE_VISUALS[state.phase];

  return (
    <span
      data-testid="furnace-build-indicator"
      data-furnace-phase={state.phase}
      aria-label={visual.ariaLabel}
      title={state.label}
      // Same shape token family as the existing chrome pills so the
      // build indicator and VM-connection pill sit side-by-side
      // without visual collision.
      className={`mr-2 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${visual.className} ${className}`.trim()}
    >
      <span aria-hidden="true" data-testid="furnace-build-indicator-glyph">
        {visual.glyph}
      </span>
      <span data-testid="furnace-build-indicator-label">{state.label}</span>
    </span>
  );
}
